/**
 * The operator's arrangement of the cockpit: which sessions are on the wall, how
 * big each tile is, the order they chose for the list, what is selected, and any
 * layouts they named.
 *
 * One hook because it was nowhere. `layout` and `attached` were plain `useState`
 * (a reload landed on a single pane, whatever had been arranged), while the two
 * fold flags beside them did persist — so the console remembered which panels
 * were open and forgot everything inside them. Putting the pieces in one place
 * also means one schema, one prune and one migration point rather than a site
 * each, drifting apart.
 *
 * Two rules the shape exists to keep:
 *
 *   - **A stored id is a claim, not a fact.** Sessions end, and a restart mints a
 *     new id. Everything read from storage is pruned against the live list
 *     before it can open a pane or join a selection, and a saved layout follows
 *     `restartOf` forward so it survives the restart of the very sessions it
 *     names.
 *   - **A prefill loses to a human** (CLAUDE.md). The list has an automatic order
 *     — waiting sessions first — and the operator may drag it into their own. Once
 *     they have, nothing re-sorts it behind them; `manualOrder` latches, exactly
 *     as `useEditable` does for a field, and `resetOrder` is the way back.
 *
 * Local, not server-side, and deliberately: `/system/settings` is a *global*
 * key/value collection with no per-operator scope, and nothing but `setup` is
 * read from it. A layout there would be one operator's arrangement imposed on
 * everyone. The console says so where it is configurable, the way the appearance
 * panel does.
 */

import { useCallback, useMemo, useRef, useState } from 'react'

import { useManualOrder } from './useManualOrder'

/** How the work area is divided. */
export type LayoutMode = 'single' | 'split' | 'grid'

/** One tile: a session, and how many tracks it takes. */
export interface WallPane {
  id: string
  /** Columns this tile spans. Clamped to SPAN_MAX — a tile wider than the wall
   *  would collapse the grid to one column and hide every other pane. */
  colSpan: number
  rowSpan: number
}

/** A layout the operator named and can bring back. */
export interface WallPreset {
  id: string
  name: string
  layout: LayoutMode
  panes: WallPane[]
  /** What each pane was called when it was saved, so a session that has since
   *  ended can be named in the placeholder rather than shown as a raw id. */
  labels?: Record<string, string>
}

export const SPAN_MAX = 2

/**
 * Where one screen's arrangement is kept.
 *
 * Keyed by screen rather than fixed, because there are two walls now — the
 * cockpit's agents and the SSH console's shells — and they are different
 * arrangements of different things. Sharing a key would mean opening one screen
 * rearranged the other, and pruning against one screen's session list would
 * silently delete the other's panes.
 */
function keysFor(scope: string) {
  return {
    layout: `kimene.${scope}.layout`,
    wall: `kimene.${scope}.wall`,
    order: `kimene.${scope}.order`,
    selected: `kimene.${scope}.selected`,
    presets: `kimene.${scope}.presets`,
    active: `kimene.${scope}.active`,
  }
}

/** Reads JSON that another version of this code, or a person, may have written.
 *  Anything unreadable is not an error worth showing — it is a preference, and
 *  the default is a correct answer. */
function read<T>(key: string, fallback: T, validate: (v: unknown) => T | null): T {
  try {
    const raw = localStorage.getItem(key)
    if (raw == null) return fallback
    return validate(JSON.parse(raw)) ?? fallback
  } catch {
    return fallback
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // A full or blocked store must not take the console down with it. The
    // arrangement stays correct for this tab; it just will not outlive it.
  }
}

function clampSpan(n: unknown): number {
  const v = typeof n === 'number' && Number.isFinite(n) ? Math.round(n) : 1
  return Math.min(Math.max(v, 1), SPAN_MAX)
}

function isMode(v: unknown): v is LayoutMode {
  return v === 'single' || v === 'split' || v === 'grid'
}

function asPanes(v: unknown): WallPane[] | null {
  if (!Array.isArray(v)) return null
  const out: WallPane[] = []
  for (const p of v) {
    // Tolerates the older shape, a bare array of ids, so an operator who had an
    // arrangement before spans existed keeps it.
    if (typeof p === 'string') out.push({ id: p, colSpan: 1, rowSpan: 1 })
    else if (p && typeof p === 'object' && typeof (p as WallPane).id === 'string') {
      const q = p as WallPane
      out.push({ id: q.id, colSpan: clampSpan(q.colSpan), rowSpan: clampSpan(q.rowSpan) })
    }
  }
  return out
}

function asIds(v: unknown): string[] | null {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : null
}

/**
 * The wall as one comparable string.
 *
 * A saved layout is only *the one on screen* for as long as the screen still
 * matches it. Without something to compare against, the tab strip would go on
 * naming a layout an operator had since detached two panes from — which is the
 * same untruth as a stale prefill, and the one thing a workspace switcher must
 * never do is misname the workspace.
 *
 * Deliberately not a count: two panes swapped for two others is a different
 * arrangement with the same number of tiles.
 */
function fingerprint(layout: LayoutMode, panes: readonly WallPane[]): string {
  return `${layout}|${panes.map((p) => `${p.id}:${p.colSpan}x${p.rowSpan}`).join(',')}`
}

/** Which layout is being worn, and what it looked like when it was put on. */
interface ActiveMark {
  id: string
  mark: string
}

function asActive(v: unknown): ActiveMark | null {
  if (!v || typeof v !== 'object') return null
  const q = v as ActiveMark
  if (typeof q.id !== 'string' || typeof q.mark !== 'string') return null
  return { id: q.id, mark: q.mark }
}

function asPresets(v: unknown): WallPreset[] | null {
  if (!Array.isArray(v)) return null
  const out: WallPreset[] = []
  for (const p of v) {
    if (!p || typeof p !== 'object') continue
    const q = p as WallPreset
    if (typeof q.id !== 'string' || typeof q.name !== 'string') continue
    out.push({
      id: q.id,
      name: q.name,
      layout: isMode(q.layout) ? q.layout : 'grid',
      panes: asPanes(q.panes) ?? [],
      labels: q.labels && typeof q.labels === 'object' ? q.labels : undefined,
    })
  }
  return out
}

/**
 * Follows a stored id forward through restarts.
 *
 * A restart stops one session and starts another, and the replacement records
 * `restartOf`. Without this a saved layout decays every time an operator
 * restarts the work it was built to watch — which is the most likely thing to
 * happen to a session someone thought worth saving a layout for.
 *
 * Iterative rather than recursive, with a visited set: a chain of restarts is
 * ordinary, and a cycle in data read off the wire must not hang the render.
 */
export function followRestarts(id: string, live: readonly { id: string; restartOf?: string }[]): string {
  const byPredecessor = new Map<string, string>()
  for (const s of live) if (s.restartOf) byPredecessor.set(s.restartOf, s.id)
  const seen = new Set<string>([id])
  let cur = id
  for (;;) {
    const next = byPredecessor.get(cur)
    if (!next || seen.has(next)) return cur
    seen.add(next)
    cur = next
  }
}

export interface LiveSession {
  id: string
  restartOf?: string
}

export interface WallLayout {
  layout: LayoutMode
  setLayout: (m: LayoutMode) => void

  /** The wall, already pruned to sessions that exist. */
  panes: WallPane[]
  setPanes: (next: WallPane[] | ((prev: WallPane[]) => WallPane[])) => void

  selected: string[]
  setSelected: (next: string[] | ((prev: string[]) => string[])) => void

  /** The operator's order, or null while the automatic one is still in charge. */
  manualOrder: string[] | null
  /** Latches: after this the automatic sort no longer applies. */
  setManualOrder: (ids: string[]) => void
  resetOrder: () => void

  presets: WallPreset[]
  /** The saved layout the wall is currently showing, or null when the
   *  arrangement on screen belongs to no named layout. */
  activePreset: string | null
  /** True once `activePreset` has been arranged away from. The strip goes on
   *  naming it — that is still where the wall came from — but it must not claim
   *  that what is on screen is what was saved. */
  presetDirty: boolean
  /** @param name what to call it; saving twice under one name replaces it.
   *  @param labelOf names each pane, so a preset can say what it lost. */
  savePreset: (name: string, labelOf?: (id: string) => string) => void
  /** Returns the panes it could not restore, by the name they had when the
   *  layout was saved. Empty when everything came back. */
  loadPreset: (id: string) => string[]
  deletePreset: (id: string) => void
}

/**
 * @param live the sessions that currently exist, used to prune every stored id.
 *   Pass the unfiltered list: pruning against a *filtered* one would delete a
 *   pane because a search box did not match it.
 */
export function useWallLayout(live: readonly LiveSession[], scope = 'lo'): WallLayout {
  // Not memoised: a handful of string concatenations per render is nothing, and
  // a memo keyed on `scope` would be one more thing that can be got wrong for
  // no gain.
  const KEYS = keysFor(scope)
  const [layout, setLayoutState] = useState<LayoutMode>(() =>
    read(KEYS.layout, 'single' as LayoutMode, (v) => (isMode(v) ? v : null)),
  )
  const [panes, setPanesState] = useState<WallPane[]>(() => read(KEYS.wall, [], asPanes))
  const [selected, setSelectedState] = useState<string[]>(() => read(KEYS.selected, [], asIds))
  // The list order is the shared primitive's: the fleet and the account pool keep
  // one the same way, and one rule about a human's decision beating a prefill
  // should have one implementation.
  const manual = useManualOrder(KEYS.order)
  const [presets, setPresetsState] = useState<WallPreset[]>(() => read(KEYS.presets, [], asPresets))
  // Persisted alongside the arrangement it names, so a reload comes back wearing
  // the same layout rather than an unnamed wall that happens to look like one.
  const [active, setActiveState] = useState<ActiveMark | null>(() =>
    read<ActiveMark | null>(KEYS.active, null, asActive),
  )

  // Ids only, and stable across renders that did not change the pool: the prune
  // below is keyed on it, and a fresh array every poll would re-run every memo
  // that depends on it five times a minute.
  const liveKey = live.map((s) => s.id).join(',')
  const liveIds = useMemo(() => new Set(liveKey ? liveKey.split(',') : []), [liveKey])

  /**
   * Nothing that no longer exists reaches the screen.
   *
   * Done on read rather than in an effect that rewrites storage. An effect would
   * make an empty first render — before `/sessions` has answered — look like a
   * pool with no sessions and erase the operator's arrangement permanently. So a
   * stored id survives an unknown pool and is only hidden while it is known to
   * be absent.
   */
  const livePanes = useMemo(
    () => (liveIds.size === 0 ? panes : panes.filter((p) => liveIds.has(p.id))),
    [panes, liveIds],
  )
  const liveSelected = useMemo(
    () => (liveIds.size === 0 ? selected : selected.filter((id) => liveIds.has(id))),
    [selected, liveIds],
  )

  const setLayout = useCallback((m: LayoutMode) => {
    setLayoutState(m)
    write(KEYS.layout, m)
  }, [])

  // The setters read and write these refs, not state, for two reasons.
  //
  // They hold the *stored* list rather than the pruned one, so a pane hidden
  // because the pool has not loaded yet is not deleted by the next edit. And
  // they are updated by the setter itself rather than during render, so a
  // caller that arranges the wall and saves it as a preset in one handler saves
  // what it just arranged — assigning during render left `savePreset` reading
  // the value from before the arrangement, and it silently saved an empty wall.
  const storedPanes = useRef(panes)
  const setPanes = useCallback((next: WallPane[] | ((prev: WallPane[]) => WallPane[])) => {
    const value = typeof next === 'function' ? next(storedPanes.current) : next
    const clean = value.map((p) => ({
      id: p.id,
      colSpan: clampSpan(p.colSpan),
      rowSpan: clampSpan(p.rowSpan),
    }))
    storedPanes.current = clean
    setPanesState(clean)
    write(KEYS.wall, clean)
  }, [])

  const storedSelected = useRef(selected)
  const setSelected = useCallback((next: string[] | ((prev: string[]) => string[])) => {
    const value = typeof next === 'function' ? next(storedSelected.current) : next
    storedSelected.current = value
    setSelectedState(value)
    write(KEYS.selected, value)
  }, [])

  const persistPresets = useCallback((next: WallPreset[]) => {
    setPresetsState(next)
    write(KEYS.presets, next)
  }, [])

  const persistActive = useCallback((next: ActiveMark | null) => {
    setActiveState(next)
    write(KEYS.active, next)
  }, [])

  const savePreset = useCallback(
    (name: string, labelOf?: (id: string) => string) => {
      // Named by the operator, keyed by name: saving twice under one name
      // replaces it, because the alternative is a list of identical labels and
      // no way to tell which is current.
      const labels: Record<string, string> = {}
      // The field the type has declared since presets existed and nothing ever
      // wrote. Without it a preset can only say *how many* of its panes are gone,
      // which is not the answer to the question an operator is asking.
      if (labelOf) for (const p of storedPanes.current) labels[p.id] = labelOf(p.id)

      const entry: WallPreset = {
        // Folded for Turkish, as the shell templates already are: "IŞIK" and
        // "ışık" are one name to the person typing them, and plain toLowerCase
        // makes them two — 'işık' and 'ışık'.
        id: name.trim().toLocaleLowerCase('tr'),
        name: name.trim(),
        layout,
        panes: storedPanes.current,
        labels: labelOf ? labels : undefined,
      }
      if (!entry.id) return
      persistPresets([...presets.filter((p) => p.id !== entry.id), entry])
      // Saving is also the way an operator updates the layout they are wearing,
      // so the wall stops reading as drifted the moment it has been written down.
      persistActive({ id: entry.id, mark: fingerprint(entry.layout, entry.panes) })
    },
    [layout, presets, persistPresets, persistActive],
  )

  const loadPreset = useCallback(
    (id: string) => {
      const preset = presets.find((p) => p.id === id)
      if (!preset) return []
      // Forward through restarts, then de-duplicated: two saved panes can both
      // have been restarted into the same session, and one session cannot hold
      // two tiles — the second would fight the first over the tmux geometry.
      const seen = new Set<string>()
      const panesNow: WallPane[] = []
      const lost: string[] = []
      for (const p of preset.panes) {
        const id2 = followRestarts(p.id, live)
        if (seen.has(id2)) continue
        seen.add(id2)
        panesNow.push({ ...p, id: id2 })
        // Only while the pool is known. An empty live list is "not loaded yet",
        // and reporting every pane as gone against it would be the same mistake
        // the prune above refuses to make.
        if (liveIds.size > 0 && !liveIds.has(id2)) lost.push(preset.labels?.[p.id] ?? p.id)
      }
      setPanes(panesNow)
      setLayout(preset.layout)
      // Marked with what was actually put on the wall, not with what the preset
      // holds: a pane followed forward through a restart has a different id, and
      // comparing against the stored one would report a fresh load as drifted.
      persistActive({ id: preset.id, mark: fingerprint(preset.layout, panesNow) })
      return lost
    },
    [presets, live, liveIds, setPanes, setLayout, persistActive],
  )

  const deletePreset = useCallback(
    (id: string) => {
      persistPresets(presets.filter((p) => p.id !== id))
      // The wall keeps whatever is on it — deleting a layout is forgetting a
      // name, not closing six terminals — but nothing may go on pointing at a
      // layout that is gone.
      if (active?.id === id) persistActive(null)
    },
    [presets, persistPresets, active, persistActive],
  )

  return {
    layout,
    setLayout,
    panes: livePanes,
    setPanes,
    selected: liveSelected,
    setSelected,
    manualOrder: manual.order,
    setManualOrder: manual.set,
    resetOrder: manual.reset,
    presets,
    // Against the stored panes rather than the pruned ones: a session ending is
    // not an edit the operator made, and reporting it as one would put a "you
    // changed this" mark on a wall nobody touched.
    activePreset: active?.id ?? null,
    presetDirty: active != null && active.mark !== fingerprint(layout, panes),
    savePreset,
    loadPreset,
    deletePreset,
  }
}

/**
 * Loading a saved layout, and saying what did not come back.
 *
 * A preset names sessions, and sessions end. Before this the missing ones were
 * simply filtered out on the way to the screen: an operator restored a layout of
 * five and got three, with nothing anywhere to say the other two had ended rather
 * than failed to draw. `labels` was declared on the type for exactly this and was
 * never written, so even the count was unavailable.
 *
 * Held for a while rather than dismissed on the next render, because it reports
 * something that happened rather than something that is true.
 */
export function usePresetLoad(loadPreset: (id: string) => string[]) {
  const [lost, setLost] = useState<string[]>([])
  const load = useCallback(
    (id: string) => setLost(loadPreset(id)),
    [loadPreset],
  )
  const dismiss = useCallback(() => setLost([]), [])
  return { load, lost, dismiss }
}
